import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {Helmet} from 'react-helmet';
import compose from 'recompose/compose';
import {window} from 'ssr-window';
import EnvironmentUtils from '@spothero/utils/environment';
import DOMUtils from '@spothero/utils/dom';
import GTMUtils from 'utils/gtm';
import Portal from '@spothero/ui/portal';
import Button from '@spothero/ui/button';
import WithConfig from 'config/WithConfig';
import Card from 'common/card/Card';
import SHLoader from 'common/loader/SHLoader';
import PriceDisplay from 'common/airport/PriceDisplay';
import {updateSelected} from 'spot/spot-actions';
import {fetchRates} from 'checkout/checkout-actions';
import {push} from 'router/router-actions';
import {updateTimes} from 'search-request/search-request-actions';
import SpotDetailsAirportHero from './hero/SpotDetailsAirportHero';
import ImportantInformation from 'common/airport/ImportantInformation';
import AmenitiesCardPanel from './panels/AmenitiesCardPanel';
import HoursCardPanel from './panels/HoursCardPanel';
import PhotosCardPanel from './panels/PhotosCardPanel';
import DirectionsCardPanel from './panels/DirectionsCardPanel';
import ThingsToKnowCardPanel from './panels/ThingsToKnowCardPanel';
import HowToParkCardPanel from './panels/HowToParkCardPanel';
import AirportPurchaseInformationDetailsOnly from './AirportPurchaseInformationDetailsOnly';

class SpotDetailsAirport extends Component {
    static propTypes = {
        match: PropTypes.shape({
            params: PropTypes.shape({
                spotId: PropTypes.string
            })
        }).isRequired,
        spot: PropTypes.object,
        isMonthly: PropTypes.bool,
        starts: PropTypes.string,
        ends: PropTypes.string,
        updateSelectedSpot: PropTypes.func,
        updateSearchTimes: PropTypes.func,
        fetchAirportRates: PropTypes.func,
        isLoading: PropTypes.bool,
        pushTo: PropTypes.func,
        isAirportRateUnavailable: PropTypes.bool
    };

    constructor(props) {
        super(props);

        this._isMobile = EnvironmentUtils.isMobileDevice();
        this._userUnsubmitted = false;
        this._spotId = parseInt(props.match.params.spotId, 10);
    }

    componentDidMount() {
        const {
            starts,
            ends,
            fetchAirportRates
        } = this.props;
        const fetchRatesParams = {
            spotId: this._spotId,
            starts,
            ends,
            airport: true,
        };

        fetchAirportRates({
            ...fetchRatesParams,
            meta: {
                ...fetchRatesParams,
                monthly: false,
                dateTimeChanged: true
            }
        });

        this._searchPageContainer = DOMUtils.el('body');

        DOMUtils.addClass(this._searchPageContainer, 'spot-details-airport-page');

        GTMUtils.push({
            event: 'pageview',
            page: 'spotdetail-airport'
        });

        window.scrollTo(0, 0);
    }

    componentWillUnmount() {
        DOMUtils.removeClass(this._searchPageContainer, 'spot-details-airport-page');
    }

    _onCheckoutClick = () => {
        const {
            spot: {
                selectedRate: {
                    fullUrl
                }
            },
            pushTo
        } = this.props;

        pushTo(fullUrl);
    }

    render() {
        const {
            spot,
            isMonthly,
            starts,
            ends,
            updateSearchTimes,
            fetchAirportRates,
            isLoading,
            pushTo,
            isAirportRateUnavailable,
            updateSelectedSpot
        } = this.props;
        let facility = null;
        let selectedRate = null;

        if (spot) {
            facility = spot.facility;
            selectedRate = spot.selectedRate;
        }

        if (isLoading || !facility) {
            return <SHLoader className="SpotDetailsAirport-loader" />;
        }

        const images = facility.airport.shuttle.images.concat(facility.images);

        return (
            <div className="SearchAirport">
                <Helmet>
                    {/* eslint-disable react/jsx-max-props-per-line */}
                    <meta name="robots" content="noindex, follow" />
                    {/* eslint-enable react/jsx-max-props-per-line */}
                </Helmet>
                <div className="SpotDetailsAirport">
                    <SpotDetailsAirportHero
                        facility={facility}
                    />
                    <div className="SpotDetailsAirport-ImportantInformation container">
                        <Card>
                            <ImportantInformation
                                data={facility.airport}
                            />
                        </Card>
                    </div>
                    <div className="SpotDetailsAirport-content container">
                        <div className="SpotDetailsAirport-panels">
                            <AmenitiesCardPanel
                                amenities={facility.amenities_full}
                            />
                            <HoursCardPanel
                                facility={facility}
                                selectedRate={selectedRate}
                            />
                            <PhotosCardPanel
                                images={images}
                            />
                            <DirectionsCardPanel
                                facility={facility}
                                selectedRate={selectedRate}
                                isMonthly={isMonthly}
                            />
                            <ThingsToKnowCardPanel
                                facility={facility}
                                selectedRate={selectedRate}
                                isMonthly={isMonthly}
                            />
                            <HowToParkCardPanel
                                facility={facility}
                                selectedRate={selectedRate}
                                isMonthly={isMonthly}
                                isOpen={false}
                                collapsible
                            />
                        </div>
                        {(this._isMobile)
                            ? (
                                <Portal>
                                    <div className="SpotDetailsAirport-snapshot">
                                        <PriceDisplay
                                            price={
                                                (selectedRate)
                                                    ? selectedRate.consumer_breakdown.price_per_day
                                                    : facility.airport.lowest_daily_rate
                                            }
                                            isDefault={!selectedRate}
                                        />
                                        <Button
                                            color="primary"
                                            onClick={this._onCheckoutClick}
                                        >
                                            Checkout
                                        </Button>
                                    </div>
                                </Portal>
                            )
                            : (
                                <div className="SpotDetailsAirport-checkout">
                                    <Card heading="Summary">
                                        <AirportPurchaseInformationDetailsOnly
                                            onCheckoutClick={this._onCheckoutClick}
                                            facility={facility}
                                            spotId={this._spotId}
                                            selectedRate={selectedRate}
                                            starts={starts}
                                            ends={ends}
                                            availableTransientInventory={facility.available_transient_inventory}
                                            fetchRates={fetchAirportRates}
                                            updateSearchTimes={updateSearchTimes}
                                            isAirportRateUnavailable={isAirportRateUnavailable}
                                            isFetchingRates={isLoading}
                                            pushTo={pushTo}
                                            updateSelectedSpot={updateSelectedSpot}
                                        />
                                    </Card>
                                </div>
                            )
                        }
                    </div>
                </div>
            </div>
        );
    }
}

const mapStateToProps = state => {
    const {
        checkout: {
            isPurchasing,
            isFetchingRates,
            isAirportRateUnavailable,
            checkoutError,
            rid,
            promoCode,
            previousRental,
            recommendedSpotsUnavailableReason,
            showChangeDateTimeModal,
            changeDateTimeModalTitle,
            changeDateTimeModalIntroMessage,
            showRecommendedSpotsModal,
            showRedundantMonthlyReservationModal,
            redundantMonthlyReservationModalDate,
            redundantMonthlyReservationModalParkingPassURL,
            showWhyTimeExtendedModal,
            purchaseForCustomer,
            isRebookingReservation,
            changedDateTimeCount,
        },
        city: {
            data: {
                phone_number: phoneNumber,
                phone_number_friendly: phoneNumberFriendly,
                monthly_phone_number: monthlyPhoneNumber,
                monthly_phone_number_friendly: monthlyPhoneNumberFriendly,
                display_name: cityDisplayName
            }
        },
        searchRequest: {
            monthly,
            airport,
            starts,
            ends,
            latitude,
            longitude,
            eid
        },
        spot: {
            selected,
            showRateDetailsModal,
            rateDetailsModalSection,
            showOversizedFeeDetailsModal,
            showOnlineCommuterRateModal
        },
        user: {
            data: userData
        }
    } = state;

    return {
        isLoading: isFetchingRates,
        isPurchasing,
        user: userData,
        checkoutError,
        isMonthly: monthly,
        isAirport: airport,
        starts,
        ends,
        latitude,
        longitude,
        eventId: eid,
        rid,
        promoCode,
        previousRental,
        spot: selected,
        showChangeDateTimeModal,
        changeDateTimeModalTitle,
        changeDateTimeModalIntroMessage,
        showRateDetailsModal,
        rateDetailsModalSection,
        showOversizedFeeDetailsModal,
        showOnlineCommuterRateModal,
        phoneNumber,
        phoneNumberFriendly,
        monthlyPhoneNumber,
        monthlyPhoneNumberFriendly,
        cityDisplayName,
        recommendedSpotsUnavailableReason,
        showRecommendedSpotsModal,
        showRedundantMonthlyReservationModal,
        redundantMonthlyReservationModalDate,
        redundantMonthlyReservationModalParkingPassURL,
        showWhyTimeExtendedModal,
        purchaseForCustomer,
        isRebookingReservation,
        changedDateTimeCount,
        isAirportRateUnavailable
    };
};

const mapDispatchToProps = {
    updateSelectedSpot: updateSelected,
    fetchAirportRates: fetchRates,
    updateSearchTimes: updateTimes,
    pushTo: push
};

const enhance = compose(
    WithConfig,
    connect(mapStateToProps, mapDispatchToProps)
);

export default enhance(SpotDetailsAirport);
